import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  bookingApi,
  clientsApi,
  vehicleTypesApi,
  addressesApi,
  quotesApi,
  tripsApi,
  type Address,
  type AssignmentPayload,
  type BookingTripResult,
  type Client,
  type TripStopInput,
  type TripTemplate,
  type VehicleType,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import {
  StopAddressPicker,
  emptyAddressSelection,
  isAddressSelectionReady,
  resolveAddressSelection,
  type AddressSelection,
} from '@/components/StopAddressPicker';
import { internalNavItems } from '@/pages/navItems';
import { AssignmentModal } from '@/components/AssignmentModal';
import { StatusBadge } from '@/components/StatusBadge';
import { useToast } from '@/components/Feedback';
import { formatPeso } from '@/lib/format';
import {
  formatManilaLocalDateTime,
  parseManilaLocalDateTime,
} from '@dispatchdash/shared';

export function BookingForm() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<BookingTripResult | null>(null);
  const toast = useToast();
  const [showAssign, setShowAssign] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const handleAssign = async (assignment: AssignmentPayload) => {
    if (!result) return;
    setAssigning(true);
    setAssignError('');
    try {
      await tripsApi.approve(result.id, assignment);
      setShowAssign(false);
      toast(`${result.tripNumber} assigned and approved`);
      navigate(`/app/dispatch/trips/${result.id}`);
    } catch (err) {
      setAssignError((err as { message?: string }).message ?? 'Failed to assign & approve');
    } finally {
      setAssigning(false);
    }
  };

  const { data: clients } = useAsync(() => clientsApi.list({ status: 'active' }), []);
  const { data: vehicleTypes } = useAsync(() => vehicleTypesApi.list(), []);
  const { data: templates } = useAsync(() => bookingApi.listTemplates(), []);

  const [templateId, setTemplateId] = useState('');
  const [templateDate, setTemplateDate] = useState(
    formatManilaLocalDateTime(new Date(Date.now() + 24 * 60 * 60 * 1000)),
  );
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');

  const handleBookFromTemplate = async () => {
    if (!templateId) return;
    setTemplateLoading(true);
    setTemplateError('');
    try {
      const trip = await bookingApi.createFromTemplate(templateId, parseManilaLocalDateTime(templateDate).toISOString());
      setResult(trip);
      toast(`Booking ${trip.tripNumber} created`);
    } catch (err) {
      setTemplateError((err as { message?: string }).message ?? 'Failed to book from template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const activeVehicleTypes = useMemo(
    () => (vehicleTypes ?? []).filter((v: VehicleType) => v.status === 'active'),
    [vehicleTypes],
  );

  const [form, setForm] = useState({
    clientId: '',
    vehicleTypeId: '',
    scheduledPickupAt: formatManilaLocalDateTime(new Date(Date.now() + 3 * 60 * 60 * 1000)),
    pickup: emptyAddressSelection() as AddressSelection | null,
    drop: emptyAddressSelection() as AddressSelection | null,
    extraDrops: [] as Array<AddressSelection | null>,
    cargoDescription: '',
    declaredPieceCount: '',
    specialInstructions: '',
    offMap: false,
    manualPrice: '',
    manualPriceBasis: '',
    creditOverride: false,
  });

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);

  const reloadAddresses = (clientId: string) => {
    setAddressesLoading(true);
    addressesApi
      .list(clientId)
      .then((rows) => setAddresses(rows))
      .catch(() => setAddresses([]))
      .finally(() => setAddressesLoading(false));
  };

  useEffect(() => {
    if (!form.clientId) {
      setAddresses([]);
      return;
    }
    reloadAddresses(form.clientId);
  }, [form.clientId]);

  const selectedClient = useMemo(
    () => (clients ?? []).find((c) => c.id === form.clientId),
    [clients, form.clientId],
  );

  const [priceEstimate, setPriceEstimate] = useState<{ amount: string; currency: string } | null>(null);
  const [priceEstimateNote, setPriceEstimateNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function estimatePrice() {
      setPriceEstimate(null);
      setPriceEstimateNote('');
      if (form.offMap || !selectedClient?.rateCardId || !form.vehicleTypeId) return;
      if (!isAddressSelectionReady(form.pickup) || !isAddressSelectionReady(form.drop)) return;

      const pickupSel = form.pickup;
      const dropSel = form.drop;
      const pickupZoneId =
        pickupSel?.kind === 'existing'
          ? addresses.find((a) => a.id === pickupSel.id)?.zoneId ?? undefined
          : undefined;
      const dropZoneId =
        dropSel?.kind === 'existing'
          ? addresses.find((a) => a.id === dropSel.id)?.zoneId ?? undefined
          : undefined;
      if (!pickupZoneId || !dropZoneId) {
        setPriceEstimateNote('Price estimate appears once both addresses are inside a service zone.');
        return;
      }

      try {
        const quote = await quotesApi.estimate({
          rateCardId: selectedClient.rateCardId,
          originZoneId: pickupZoneId,
          destinationZoneId: dropZoneId,
          vehicleTypeId: form.vehicleTypeId,
          stops: 1 + form.extraDrops.filter(isAddressSelectionReady).length,
        });
        if (!cancelled) setPriceEstimate(quote);
      } catch (err) {
        if (!cancelled) {
          setPriceEstimateNote((err as { message?: string }).message ?? 'Could not estimate price');
        }
      }
    }
    void estimatePrice();
    return () => {
      cancelled = true;
    };
  }, [
    form.clientId,
    form.vehicleTypeId,
    form.pickup,
    form.drop,
    form.extraDrops,
    form.offMap,
    selectedClient,
    addresses,
  ]);

  const canOverrideCredit = hasPermission('clients.credit_override');
  const canAssignResult = Boolean(
    result &&
      result.status === 'requested' &&
      (!result.quoteRequired || result.quoteAccepted) &&
      hasPermission('trips.confirm'),
  );

  const addExtraDrop = () => {
    setForm((f) => ({ ...f, extraDrops: [...f.extraDrops, null] }));
  };

  const updateExtraDrop = (index: number, value: AddressSelection | null) => {
    setForm((f) => {
      const next = [...f.extraDrops];
      next[index] = value;
      return { ...f, extraDrops: next };
    });
  };

  const removeExtraDrop = (index: number) => {
    setForm((f) => ({
      ...f,
      extraDrops: f.extraDrops.filter((_, i) => i !== index),
    }));
  };

  const resetForNewBooking = () => {
    setResult(null);
    setForm((f) => ({
      ...f,
      pickup: null,
      drop: null,
      extraDrops: [],
      cargoDescription: '',
      declaredPieceCount: '',
      specialInstructions: '',
      offMap: false,
      manualPrice: '',
      manualPriceBasis: '',
      creditOverride: false,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isAddressSelectionReady(form.pickup) || !isAddressSelectionReady(form.drop)) {
      setError('Choose or type a pickup and at least one drop address');
      return;
    }
    if (form.offMap && !form.manualPrice) {
      setError('Off-map trips require a manual price');
      return;
    }

    setLoading(true);
    try {
      const remember = (created: Address) => {
        setAddresses((prev) => (prev.some((a) => a.id === created.id) ? prev : [...prev, created]));
      };

      const dropSelections = [form.drop!, ...form.extraDrops.filter(isAddressSelectionReady)] as AddressSelection[];
      const pickupFields = await resolveAddressSelection(form.pickup!, form.clientId, remember);
      const dropFields = await Promise.all(
        dropSelections.map((sel) => resolveAddressSelection(sel, form.clientId, remember)),
      );

      const stops: TripStopInput[] = [
        { sequence: 1, type: 'pickup', ...pickupFields },
        ...dropFields.map((fields, i) => ({
          sequence: i + 2,
          type: 'drop' as const,
          ...fields,
        })),
      ];

      const trip = await bookingApi.createTrip({
        clientId: form.clientId,
        scheduledPickupAt: parseManilaLocalDateTime(form.scheduledPickupAt).toISOString(),
        vehicleTypeId: form.vehicleTypeId,
        stops,
        cargoDescription: form.cargoDescription || undefined,
        declaredPieceCount: form.declaredPieceCount ? Number(form.declaredPieceCount) : undefined,
        specialInstructions: form.specialInstructions || undefined,
        offMap: form.offMap || undefined,
        manualPrice: form.manualPrice || undefined,
        manualPriceBasis: form.manualPriceBasis || undefined,
        creditOverride: form.creditOverride || undefined,
      });
      setResult(trip);
      toast(`Booking ${trip.tripNumber} created`);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout navItems={internalNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/app/dispatch')}>← Board</Button>
            <h1>New booking</h1>
            <p className="text-muted">Create an internal trip booking for a client</p>
          </div>
          <Button type="button" variant="secondary" onClick={() => navigate('/app/dispatch/book/bulk')}>
            Bulk upload
          </Button>
        </div>

        {!result && (templates ?? []).length > 0 && (
          <div className="booking-form template-panel">
            <h3>Book from template</h3>
            <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
              Reuse a saved trip shape — just pick a new pickup date.
            </p>
            {templateError && <div className="form-error">{templateError}</div>}
            <div className="form-row">
              <Select label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Select a template…</option>
                {(templates ?? []).map((t: TripTemplate) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
              <Input
                id="template-scheduled-pickup"
                label="Scheduled pickup"
                type="datetime-local"
                value={templateDate}
                onChange={(e) => setTemplateDate(e.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              loading={templateLoading}
              disabled={!templateId}
              onClick={handleBookFromTemplate}
            >
              Create from template
            </Button>
          </div>
        )}

        {result ? (
          <div className="booking-form quote-panel">
            <h3>Booking created — {result.tripNumber}</h3>
            <dl className="detail-list">
              <dt>Status</dt><dd><StatusBadge status={result.status} size="sm" /></dd>
              <dt>Quoted amount</dt>
              <dd>
                {result.quoteRequired && !result.quoteAccepted
                  ? 'Pending quote'
                  : formatPeso(result.quotedAmount ?? '0')}
              </dd>
              {result.quoteRequired && (
                <>
                  <dt>Quote status</dt>
                  <dd>{result.quoteAccepted ? 'Accepted' : 'Awaiting acceptance'}</dd>
                </>
              )}
              {result.offMap && (<><dt>Off-map</dt><dd>Yes — manual pricing applied</dd></>)}
            </dl>
            {canAssignResult ? (
              <p className="text-muted" style={{ marginTop: 'var(--space-md)' }}>
                Next: assign a vehicle and driver to confirm this trip.
              </p>
            ) : result.status === 'requested' && result.quoteRequired && !result.quoteAccepted ? (
              <p className="text-muted" style={{ marginTop: 'var(--space-md)' }}>
                Next: open the trip to finish the quote before it can be assigned.
              </p>
            ) : null}
            <div className="form-actions">
              {canAssignResult && (
                <Button type="button" onClick={() => { setAssignError(''); setShowAssign(true); }}>
                  Assign vehicle & crew
                </Button>
              )}
              <Button
                type="button"
                variant={canAssignResult ? 'secondary' : 'primary'}
                onClick={() => navigate(`/app/dispatch/trips/${result.id}`)}
              >
                View trip
              </Button>
              <Button type="button" variant="secondary" onClick={resetForNewBooking}>Book another</Button>
            </div>
            <AssignmentModal
              open={showAssign}
              onClose={() => setShowAssign(false)}
              onSubmit={handleAssign}
              loading={assigning}
              submitError={assignError}
              trip={{
                label: result.tripNumber,
                clientName: (clients ?? []).find((c) => c.id === result.clientId)?.legalName ?? null,
                vehicleTypeId: result.vehicleTypeId,
                vehicleTypeName: (vehicleTypes ?? []).find((v) => v.id === result.vehicleTypeId)?.name ?? null,
              }}
            />
          </div>
        ) : (
          <form className="booking-form" onSubmit={handleSubmit}>
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="client">Client</label>
              <select
                id="client"
                className="select-field"
                value={form.clientId}
                onChange={(e) =>
                  setForm({
                    ...form,
                    clientId: e.target.value,
                    pickup: null,
                    drop: null,
                    extraDrops: [],
                  })
                }
                required
              >
                <option value="">Select client…</option>
                {(clients ?? []).map((c: Client) => (
                  <option key={c.id} value={c.id}>{c.legalName}</option>
                ))}
              </select>
              {clients?.length === 0 && (
                <span className="input-hint">No active clients yet — add one in Admin → Clients</span>
              )}
            </div>

            <div className="form-row">
              <StopAddressPicker
                label="Pickup address"
                clientId={form.clientId}
                addresses={addresses}
                value={form.pickup}
                onChange={(pickup) => setForm({ ...form, pickup })}
                disabled={!form.clientId || addressesLoading}
                required
              />
              <StopAddressPicker
                label="Drop address"
                clientId={form.clientId}
                addresses={addresses}
                value={form.drop}
                onChange={(drop) => setForm({ ...form, drop })}
                disabled={!form.clientId || addressesLoading}
                required
              />
            </div>

            {form.extraDrops.map((selection, i) => (
              <div className="form-row" key={i}>
                <StopAddressPicker
                  label={`Extra drop ${i + 1}`}
                  clientId={form.clientId}
                  addresses={addresses}
                  value={selection}
                  onChange={(value) => updateExtraDrop(i, value)}
                  disabled={!form.clientId || addressesLoading}
                />
                <Button type="button" variant="ghost" onClick={() => removeExtraDrop(i)}>Remove</Button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addExtraDrop} disabled={!form.clientId}>
              + Add extra drop
            </Button>

            <p className="input-hint" style={{ marginTop: 'var(--space-sm)' }}>
              Search the client address book, or type a new address and optionally save it.
            </p>

            <div className="form-row" style={{ marginTop: 'var(--space-md)' }}>
              <Select
                label="Vehicle type"
                value={form.vehicleTypeId}
                onChange={(e) => setForm({ ...form, vehicleTypeId: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {activeVehicleTypes.map((v: VehicleType) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </Select>
              <Input
                id="booking-scheduled-pickup"
                label="Scheduled pickup"
                type="datetime-local"
                value={form.scheduledPickupAt}
                onChange={(e) => setForm({ ...form, scheduledPickupAt: e.target.value })}
                required
              />
            </div>

            <div className="form-row">
              <Input
                label="Cargo description"
                value={form.cargoDescription}
                onChange={(e) => setForm({ ...form, cargoDescription: e.target.value })}
                placeholder="20 pallets, dry goods"
              />
              <Input
                label="Declared piece count"
                type="number"
                min="0"
                hint="Drivers confirm this count at pickup (and at drop-off for single-drop trips)"
                value={form.declaredPieceCount}
                onChange={(e) => setForm({ ...form, declaredPieceCount: e.target.value })}
              />
            </div>

            <Input
              label="Special instructions"
              value={form.specialInstructions}
              onChange={(e) => setForm({ ...form, specialInstructions: e.target.value })}
              placeholder="Notes for the crew…"
            />

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={form.offMap}
                onChange={(e) => setForm({ ...form, offMap: e.target.checked })}
              />
              Off-map / non-standard route (requires manual price)
            </label>

            {form.offMap && (
              <div className="form-row">
                <Input
                  label="Manual price (₱)"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={form.manualPrice}
                  onChange={(e) => setForm({ ...form, manualPrice: e.target.value })}
                  placeholder="0.00"
                  required
                />
                <Input
                  label="Manual price basis"
                  value={form.manualPriceBasis}
                  onChange={(e) => setForm({ ...form, manualPriceBasis: e.target.value })}
                  placeholder="Reason / approval reference"
                />
              </div>
            )}

            {canOverrideCredit && (
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={form.creditOverride}
                  onChange={(e) => setForm({ ...form, creditOverride: e.target.checked })}
                />
                Override client credit limit block
              </label>
            )}

            {(priceEstimate || priceEstimateNote) && !form.offMap && (
              <div className="quote-panel" style={{ marginTop: 'var(--space-md)' }}>
                <h3>Estimated price</h3>
                {priceEstimate ? (
                  <p>
                    <strong>{formatPeso(priceEstimate.amount)}</strong>
                    <span className="text-muted"> before confirm (excludes accessorials)</span>
                  </p>
                ) : (
                  <p className="text-muted">{priceEstimateNote}</p>
                )}
              </div>
            )}

            <div className="form-actions">
              <Button variant="secondary" onClick={() => navigate('/app/dispatch')} type="button">Cancel</Button>
              <Button type="submit" loading={loading}>Create booking</Button>
            </div>
          </form>
        )}

        <style>{`
          .booking-form {
            max-width: 680px;
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-xl);
          }
          .select-field {
            width: 100%;
            padding: var(--space-sm) var(--space-md);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            background: var(--color-surface);
            font-size: 1rem;
          }
          .form-error {
            background: #FDE8E8;
            color: var(--color-urgent);
            padding: var(--space-sm) var(--space-md);
            border-radius: var(--radius-sm);
            margin-bottom: var(--space-md);
            font-size: 0.875rem;
          }
          .form-actions {
            display: flex;
            gap: var(--space-md);
            margin-top: var(--space-lg);
          }
          .checkbox-row {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
            margin: var(--space-md) 0;
            font-size: 0.9375rem;
            cursor: pointer;
          }
          .template-panel {
            margin-bottom: var(--space-lg);
          }
          .template-panel h3 { color: var(--color-forest); margin-bottom: var(--space-xs); }
          .quote-panel h3 { color: var(--color-forest); margin-bottom: var(--space-md); }
          .detail-list {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: var(--space-sm) var(--space-md);
            font-size: 0.9375rem;
          }
          .detail-list dt { font-weight: 500; color: var(--color-text-muted); }
          .form-row {
            display: flex;
            flex-wrap: wrap;
            gap: var(--space-md);
            align-items: flex-start;
          }
          .form-row > * { flex: 1 1 220px; min-width: 0; }
          .form-row > .btn { flex: 0 0 auto; }
          .form-actions { flex-wrap: wrap; }
        `}</style>
      </div>
    </Layout>
  );
}
